/**
 * @file: TaskFlowStatusUpdateBody.tsx
 *
 * `OrgTaskFlowStatusUpdate` — a single flow stage finished / started /
 * was cancelled. The descriptor folds the type + status into the
 * category chip ("施工任务开始" / "施工任务完成" / etc., colored by
 * status), so the body doesn't repeat them — it just renders the
 * subject (flow name for per-flow events, a static label for the
 * task-wide ones) + remark + media.
 */

import { TaskFlowType } from '@euler/model/enum/TaskFlowType';
import { OrgTaskFlowStatusUpdateFeedPayload } from '@euler/model/feed';
import { FC } from 'react';
import { View } from 'react-native';
import { MediaStrip } from '../MediaStrip';
import { BodyText, TitleText } from './common/primitives';

/**
 * Resolve the subject line for a task-flow event. For the per-flow
 * variants (`InspectionFlow`, `ConstructionJob`) the producer puts the
 * specific name on `refs.flowName`; the task-wide variants don't have
 * an instance name so we fall back to a static label. Mirrors the
 * mapping the backend producer's push body uses
 * (`formatTaskFlowStatusUpdateNotificationBody`).
 */
const flowSubjectLabel = (
  flowType: TaskFlowType,
  flowName: string | undefined,
): string => {
  switch (flowType) {
    case TaskFlowType.PreInspection:
      return '接车预检';
    case TaskFlowType.Inspection:
      return '维修 / 保养检测';
    case TaskFlowType.InspectionFlow:
      return flowName ?? '检测流程';
    case TaskFlowType.Construction:
      return '全部施工项目';
    case TaskFlowType.ConstructionJob:
      return flowName ?? '施工任务';
    case TaskFlowType.CompletionCheck:
      return '竣工检验';
    case TaskFlowType.SamplingCheck:
      return '完工抽检';
    default:
      return flowName ?? '工单流程';
  }
};

export const TaskFlowStatusUpdateBody: FC<{
  p: OrgTaskFlowStatusUpdateFeedPayload;
}> = ({ p }) => {
  const subject = flowSubjectLabel(p.refs.flowType, p.refs.flowName);
  return (
    <View>
      <TitleText>{subject}</TitleText>
      {p.remark ? <BodyText mt={6}>{p.remark}</BodyText> : null}
      {p.medias && p.medias.length > 0 ? (
        <View style={{ marginTop: 10 }}>
          <MediaStrip medias={p.medias} size={56} max={4} gap={6} previewable />
        </View>
      ) : null}
    </View>
  );
};
